import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import Tag from 'atoms/Tag/Tag';
import ChevronDownIcon from 'images/v5/icons/chevron-down.svg';
import CloseSvg from 'images/v5/icons/x-close.svg';
import Avatar from 'layout/atoms/Avatar/Avatar';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Loader from 'layout/atoms/Loader/Loader';
import parseImage from 'parsers/common/parseImage';
import styles from './Select.module.scss';
function Select({ options, value, onChange, placeholder = 'Select', multiselect = false, theme = 'tags', disabled = false, loading = false, valueLabel, name, className, fullWidth = false, testId, error, onBlur, onOpen, }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);
    const selectedValues = multiselect && Array.isArray(value) ? value : [];
    const selectedValue = !multiselect && !Array.isArray(value) ? value : undefined;
    const getOptionLabel = (val) => { var _a, _b; return (_b = (_a = options.find((o) => o.value === val)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : ''; };
    const selectedLabel = !multiselect && selectedValue !== undefined
        ? getOptionLabel(selectedValue) || valueLabel || ''
        : '';
    const filteredOptions = options.filter((o) => { var _a; return ((_a = o.label) !== null && _a !== void 0 ? _a : '').toLowerCase().includes(search.toLowerCase()); });
    const hasAnyThumbnail = options.some((o) => o.thumbnail);
    const isSelected = (optValue) => {
        if (multiselect)
            return selectedValues.includes(optValue);
        return selectedValue === optValue;
    };
    const open = () => {
        if (disabled)
            return;
        setIsOpen(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        setTimeout(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
    };
    const close = () => {
        setIsOpen(false);
        setSearch('');
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const handleContainerClick = () => {
        if (isOpen)
            return;
        open();
    };
    const handleSelect = (optValue) => {
        if (multiselect) {
            const next = selectedValues.includes(optValue)
                ? selectedValues.filter((v) => v !== optValue)
                : [...selectedValues, optValue];
            onChange(next);
            setSearch('');
        }
        else {
            onChange(optValue);
            close();
        }
    };
    const handleRemoveTag = (optValue) => {
        onChange(selectedValues.filter((v) => v !== optValue));
    };
    const handleClearAll = (e) => {
        e.stopPropagation();
        onChange(multiselect ? [] : '');
        setSearch('');
    };
    const handleChevronClick = (e) => {
        e.stopPropagation();
        if (isOpen) {
            close();
        }
        else {
            open();
        }
    };
    const hasValue = multiselect ? selectedValues.length > 0 : Boolean(selectedLabel);
    const containerClasses = classNames(styles.container, {
        [styles.open]: isOpen,
        [styles.fullWidth]: fullWidth,
        [styles.disabled]: disabled,
        [styles.error]: error,
    }, className);
    const sharedInputProps = {
        ref: inputRef,
        className: styles.searchInput,
        disabled,
        name,
        autoComplete: 'off',
        onFocus: open,
        onClick: (e) => e.stopPropagation(),
        onChange: (e) => setSearch(e.target.value),
    };
    const renderContent = () => {
        if (multiselect && theme === 'tags') {
            const visibleTags = selectedValues
                .map((val) => ({ val, label: getOptionLabel(val) }))
                .filter(({ label }) => Boolean(label));
            return (React.createElement(React.Fragment, null,
                loading && selectedValues.length > 0 && visibleTags.length === 0 ? (React.createElement("span", { className: styles.loadingCount },
                    selectedValues.length,
                    " selected")) : (visibleTags.map(({ val, label }) => (React.createElement(Tag, { key: val, label: label, onRemove: () => handleRemoveTag(val), disabled: disabled })))),
                React.createElement("input", Object.assign({}, sharedInputProps, { value: search, placeholder: selectedValues.length === 0 ? placeholder : '' }))));
        }
        if (multiselect && theme === 'aggregated') {
            return (React.createElement("input", Object.assign({}, sharedInputProps, { value: search ||
                    (selectedValues.length > 0 && !isOpen ? `${selectedValues.length} values selected` : ''), placeholder: placeholder })));
        }
        return (React.createElement("input", Object.assign({}, sharedInputProps, { value: search || (!isOpen ? selectedLabel : ''), placeholder: placeholder })));
    };
    return (React.createElement(Dropdown, { controlled: true, open: isOpen, shape: fullWidth ? 'block' : 'max-content', position: "bottomLeft", matchTriggerWidth: true, onClose: close, testId: testId, trigger: React.createElement("div", { className: containerClasses, role: "combobox", "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-controls": "select-dropdown", tabIndex: disabled ? -1 : 0, onClick: handleContainerClick, onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ')
                    handleContainerClick();
            } },
            React.createElement("div", { className: styles.content }, renderContent()),
            React.createElement("div", { className: styles.actions },
                hasValue && !loading && (React.createElement("button", { type: "button", className: styles.actionButton, onClick: handleClearAll, disabled: disabled, tabIndex: -1 },
                    React.createElement(CloseSvg, null))),
                loading ? (React.createElement("span", { className: styles.loaderIcon },
                    React.createElement(Loader, { size: "xs", noMargin: true }))) : (React.createElement("button", { type: "button", className: classNames(styles.actionButton, { [styles.chevronOpen]: isOpen }), onClick: handleChevronClick, disabled: disabled, tabIndex: -1 },
                    React.createElement(ChevronDownIcon, null))))) },
        React.createElement(React.Fragment, null,
            loading && React.createElement(Loader, { size: "s", noMargin: true }),
            !loading && filteredOptions.length === 0 && (React.createElement("div", { className: styles.noResults }, "No results")),
            !loading &&
                filteredOptions.map((option) => {
                    var _a;
                    return (React.createElement(Button, { key: option.value, align: "left", theme: isSelected(option.value) ? 'fill' : 'ghost', leftIcon: hasAnyThumbnail ? (React.createElement(Avatar, { src: (_a = parseImage(option.thumbnail)) !== null && _a !== void 0 ? _a : undefined, type: "image", size: "xs", shape: "square", userName: option.label })) : undefined, onClick: () => handleSelect(option.value) }, option.label));
                }))));
}
export default Select;
